import React, { useRef } from 'react';
import { IconCamera } from '@tabler/icons-react';
import TiltIndicator from './TiltIndicator';
import styles from './CaptureCoach.module.scss';

interface CaptureCoachProps {
    // Fires with the full-resolution image the OS camera returns.
    onFile: (file: File) => void;
}

// Pre-capture coaching step. The web can't grab full sensor resolution from a
// live getUserMedia stream, so instead of a low-res in-app viewfinder we coach
// the framing/leveling here (with the live tilt guide) and then hand off to the
// device camera, which returns a full-resolution photo.
const CaptureCoach = ({ onFile }: CaptureCoachProps) => {
    const cameraRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file after a retake
        if (file && file.type.startsWith('image/')) onFile(file);
    };

    return (
        <div className={styles.coach}>
            <div className={styles.guide}>
                <TiltIndicator />
            </div>

            <div className={styles.tips}>
                <span className={styles.tipsTitle}>Before you shoot</span>
                <ul className={styles.tipList}>
                    <li>Fill the frame with the aperture screen — get close and keep it square.</li>
                    <li>Hold the device level using the guide above.</li>
                    <li>Keep steady and make sure it's well lit.</li>
                </ul>
                <span className={styles.note}>
                    Opens your device camera so the photo is captured at full resolution.
                </span>
            </div>

            <button type="button" className={styles.openBtn} onClick={() => cameraRef.current?.click()}>
                <IconCamera size={22} />
                <span>Open Camera</span>
            </button>

            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleChange}
            />
        </div>
    );
};

export default CaptureCoach;
